
import React from 'react';
import { Wallet, ShieldCheck, Zap, BarChart3, ChevronRight, Activity, Globe } from 'lucide-react';

interface LandingPageProps {
  onStart: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onStart }) => {
  return (
    <div className="min-h-screen bg-navy text-white font-sans overflow-hidden selection:bg-electric/30">
      {/* Background Decor */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-electric/10 rounded-full blur-[120px] -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-sky/5 rounded-full blur-[120px] translate-y-1/2"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-50 contrast-150"></div>
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.03) 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-32">
        {/* Header */}
        <header className="flex justify-between items-center mb-24 animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center shadow-lg shadow-electric/20 bg-white">
              <img 
                src="https://lh3.googleusercontent.com/d/1V43bsQ1MSLTCV946PqQyzxJ8w55-MtJc" 
                alt="StackUp Logo" 
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            </div>
            <span className="font-extrabold text-2xl tracking-tight">Stack<span className="text-electric">Up</span></span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
            <a href="#" className="hover:text-electric transition-colors">Architecture</a>
            <a href="#" className="hover:text-electric transition-colors">Intelligence</a>
            <a href="#" className="hover:text-electric transition-colors">Security</a>
          </div>
        </header>

        {/* Hero Section */}
        <div className="max-w-3xl mb-32">
          <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-[0.9] mb-8 animate-slide-up">
            Take Control of <br />
            <span className="text-electric">Your Money</span>.
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 font-medium leading-relaxed mb-10 animate-slide-up [animation-delay:200ms]">
            A simple, private, and powerful way to track your spending, plan your budget, and grow your savings. No confusing bank talk, just clear steps to financial peace.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-slide-up [animation-delay:400ms]">
            <button 
              onClick={onStart}
              className="px-10 py-5 bg-electric hover:bg-white hover:text-navy rounded-2xl font-black text-sm uppercase tracking-widest transition-all shadow-xl shadow-electric/20 active:scale-95 flex items-center justify-center gap-3 group"
            >
              Get Started
              <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <div className="flex items-center gap-4 px-6 py-4 rounded-2xl border border-white/10 backdrop-blur-sm">
              <div className="flex -space-x-2">
                {[1,2,3].map(i => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-navy bg-slate-800 flex items-center justify-center">
                    <Activity size={14} className="text-electric" />
                  </div>
                ))}
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Trusted by many</span>
            </div>
          </div>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-slide-up [animation-delay:600ms]">
          <FeatureCard 
            icon={<Zap className="text-electric" size={24} />}
            title="Simple Tracking"
            desc="Easily log your daily spending and income. See exactly where your money goes every month."
          />
          <FeatureCard 
            icon={<ShieldCheck className="text-sky" size={24} />}
            title="Wealth Overview"
            desc="Keep track of your savings, investments, and debts in one place to see your total value grow."
          />
          <FeatureCard 
            icon={<Globe className="text-emerald-400" size={24} />}
            title="Smart Insights"
            desc="Get helpful tips and see patterns in your spending to help you save more for what matters."
          />
        </div>

        {/* Power Tools Section */}
        <div className="mt-40 border-t border-white/5 pt-20">
          <div className="flex flex-col md:flex-row gap-20 items-center">
            <div className="flex-1 space-y-6">
              <h3 className="text-3xl font-extrabold tracking-tight italic">"The best way to predict your future is to create it."</h3>
              <p className="text-slate-400 text-lg">Our extra tools help you plan for emergencies, analyze your investment mix, and even organize your long-term legacy for your loved ones.</p>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-4 w-full">
              <div className="p-8 bg-white/5 rounded-3xl border border-white/10 hover:border-electric/30 transition-colors">
                <BarChart3 className="text-electric mb-4" />
                <div className="text-xs font-black uppercase tracking-widest">Growth Sim</div>
              </div>
              <div className="p-8 bg-white/5 rounded-3xl border border-white/10 hover:border-electric/30 transition-colors mt-8">
                <Activity className="text-sky mb-4" />
                <div className="text-xs font-black uppercase tracking-widest">Health Score</div>
              </div>
            </div>
          </div>
        </div>

        <footer className="mt-40 pt-10 border-t border-white/5 text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-600">
            &copy; {new Date().getFullYear()} StackUp Intelligence Systems. Secure • Private • Local.
          </p>
        </footer>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, desc }: { icon: React.ReactNode, title: string, desc: string }) => (
  <div className="p-10 bg-white/5 border border-white/10 rounded-4xl hover:bg-white/[0.07] transition-all group">
    <div className="mb-6 p-4 bg-navy rounded-2xl border border-white/5 w-fit group-hover:scale-110 transition-transform">
      {icon}
    </div>
    <h3 className="text-xl font-bold mb-4">{title}</h3>
    <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
  </div>
);
